// components/Filters.js
import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  TextField,
  Slider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  IconButton,
  useTheme,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import { motion, AnimatePresence } from "framer-motion";
import { AMENITIES_MAP, AMENITY_EMOJIS } from "../../utils/amenitiesMap";

// ── Shared section header ────────────────────────────────────────────────────
function SectionLabel({ children }) {
  return (
    <Typography
      variant="subtitle2"
      fontWeight={700}
      sx={{
        mb: 1.5,
        color: "text.secondary",
        letterSpacing: "0.6px",
        fontSize: "0.72rem",
        textTransform: "uppercase",
        textAlign: "center",
      }}
    >
      {children}
    </Typography>
  );
}

// ── Shared chip toggle helper ────────────────────────────────────────────────
function FilterChip({ label, selected, onClick }) {
  const theme = useTheme();
  return (
    <Chip
      label={label}
      clickable
      onClick={onClick}
      variant={selected ? "filled" : "outlined"}
      color={selected ? "primary" : "default"}
      size="small"
      sx={{
        fontWeight: selected ? 600 : 400,
        borderColor: selected ? "transparent" : theme.palette.divider,
        borderRadius: "8px",
        px: 0.5,
        transition: "all 0.15s ease",
        "&:hover": {
          transform: "translateY(-1px)",
          boxShadow: selected
            ? `0 2px 8px ${theme.palette.primary.main}40`
            : `0 2px 6px ${theme.palette.action.hover}`,
        },
      }}
    />
  );
}

// ── Section wrapper for consistent spacing ───────────────────────────────────
function FilterSection({ children, noDivider = false }) {
  return (
    <>
      <Box sx={{ py: 2.5 }}>{children}</Box>
      {!noDivider && <Divider />}
    </>
  );
}

export default function AdvancedFiltersModal({
  open,
  onClose,
  searchType,
  advancedFilters,
  setAdvancedFilters,
  airbnbType,
  setAirbnbType,
  minReviewScore,
  setMinReviewScore,
  minReviewCount,
  setMinReviewCount,
}) {
  const theme = useTheme();

  const toggleFlightStop = (value) => {
    setAdvancedFilters((prev) => {
      const current = prev.flightStops || [];
      return {
        ...prev,
        flightStops: current.includes(value)
          ? current.filter((s) => s !== value)
          : [...current, value],
      };
    });
  };

  const toggleCancelPolicy = (policy) => {
    setAdvancedFilters((prev) => {
      const current = prev.cancelPolicies || [];
      return {
        ...prev,
        cancelPolicies: current.includes(policy)
          ? current.filter((p) => p !== policy)
          : [...current, policy],
      };
    });
  };

  const toggleAmenity = (key) => {
    setAdvancedFilters((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const POPULAR_AMENITY_IDS = [
    1, 4, 5, 8, 9, 12, 23, 33, 34, 53, 91, 92, 95, 96, 258, 25, 132, 47, 101,
    100, 45, 30, 77,
  ];

  const sortChips = (ids) =>
    [...ids].sort((a, b) => {
      const aKey = `amenity_${a}`;
      const bKey = `amenity_${b}`;
      const aSelected = !!advancedFilters[aKey];
      const bSelected = !!advancedFilters[bKey];
      if (aSelected !== bSelected) return bSelected - aSelected;
      return (AMENITIES_MAP[a] || "").localeCompare(AMENITIES_MAP[b] || "");
    });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          maxHeight: "85vh",
          overflow: "hidden",
        },
      }}
    >
      {/* ── Header ── */}
      <DialogTitle
        sx={{
          bgcolor: "background.paper",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 2,
          px: 3,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography variant="h6" fontWeight={700} fontSize="1.1rem">
          Filters
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            bgcolor: theme.palette.action.hover,
            "&:hover": { bgcolor: theme.palette.action.selected },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          bgcolor: "background.paper",
          px: { xs: 2, sm: 4 },
          py: 0,
          overflowY: "auto",
        }}
      >

        {/* ── ✈️ Flight Stops + 🏠 Airbnb Type (grouped row) ── */}
        <FilterSection>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 3, sm: 4 },
            }}
          >
            {/* Flight Stops — only in flight modes */}
            {searchType !== "staysOnly" && (
              <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
                <SectionLabel>✈️ Flight Stops</SectionLabel>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "center" }}>
                  {[
                    { value: "nonstop", label: "Non-stop" },
                    { value: "1stop",   label: "1 Stop" },
                    { value: "2plus",   label: "2+ Stops" },
                  ].map(({ value, label }) => (
                    <FilterChip
                      key={value}
                      label={label}
                      selected={(advancedFilters.flightStops || []).includes(value)}
                      onClick={() => toggleFlightStop(value)}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Airbnb Type */}
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
              <SectionLabel>🏠 Type of Airbnb</SectionLabel>
              <ToggleButtonGroup
                value={airbnbType}
                exclusive
                onChange={(_, v) => { if (v !== null) setAirbnbType(v); }}
                sx={{
                  width: "100%",
                  "& .MuiToggleButton-root": {
                    flex: 1,
                    textTransform: "none",
                    fontSize: "0.85rem",
                    fontWeight: 500,
                    py: 1,
                    border: `1.5px solid ${theme.palette.divider}`,
                    transition: "all 0.15s ease",
                    "&.Mui-selected": {
                      backgroundColor: theme.palette.primary.main,
                      color: "#fff",
                      borderColor: theme.palette.primary.main,
                      fontWeight: 600,
                      "&:hover": { backgroundColor: theme.palette.primary.dark },
                    },
                    "&:not(:first-of-type)": { borderLeft: "none" },
                    "&:first-of-type": { borderRadius: "10px 0 0 10px" },
                    "&:last-of-type":  { borderRadius: "0 10px 10px 0" },
                  },
                }}
              >
                <ToggleButton value="both">Any</ToggleButton>
                <ToggleButton value="entire_home">Entire Home</ToggleButton>
                <ToggleButton value="private_room">Private Room</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>
        </FilterSection>

        {/* ── 💰 Price  |  🚫 Cancellation + ⭐ Rating + 💬 Reviews ── */}
        <FilterSection>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 3, sm: 4 },
            }}
          >
            {/* Left: Nightly Price */}
            <Box sx={{ flex: 1 }}>
              <SectionLabel>💰 Nightly Price (USD)</SectionLabel>
              <Box sx={{ px: 1 }}>
                <Slider
                  value={[advancedFilters.minPrice ?? 0, advancedFilters.maxPrice ?? 300]}
                  onChange={(_, v) =>
                    setAdvancedFilters((prev) => ({ ...prev, minPrice: v[0], maxPrice: v[1] }))
                  }
                  valueLabelDisplay="auto"
                  min={0}
                  max={300}
                  step={10}
                  valueLabelFormat={(v) => (v === 300 ? "$300+" : `$${v}`)}
                  sx={{ mb: 1 }}
                />
                <Box sx={{ display: "flex", gap: 2 }}>
                  <TextField
                    label="Min ($)"
                    type="number"
                    size="small"
                    value={advancedFilters.minPrice ?? 0}
                    onChange={(e) =>
                      setAdvancedFilters((prev) => ({
                        ...prev,
                        minPrice: e.target.value === "" ? null : parseInt(e.target.value, 10),
                      }))
                    }
                    sx={{ flex: 1 }}
                    slotProps={{ htmlInput: { min: 0 } }}
                  />
                  <TextField
                    label="Max ($)"
                    type="text"
                    size="small"
                    value={advancedFilters.maxPrice === 300 ? "Inf" : (advancedFilters.maxPrice ?? 300)}
                    onChange={(e) => {
                      const input = e.target.value.toLowerCase();
                      if (input === "inf") {
                        setAdvancedFilters((prev) => ({ ...prev, maxPrice: 300 }));
                      } else {
                        const val = parseInt(input.replace(/\D/g, ""), 10);
                        if (!isNaN(val))
                          setAdvancedFilters((prev) => ({ ...prev, maxPrice: Math.min(val, 300) }));
                      }
                    }}
                    onBlur={(e) => {
                      const val = e.target.value.toLowerCase();
                      if (val === "" || isNaN(parseInt(val)))
                        setAdvancedFilters((prev) => ({ ...prev, maxPrice: 300 }));
                    }}
                    sx={{ flex: 1 }}
                    slotProps={{ htmlInput: { inputMode: "numeric", style: { textAlign: "center" } } }}
                  />
                </Box>
              </Box>
            </Box>

            {/* Right: Cancellation + Rating + Reviews stacked */}
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 2.5 }}>
              {/* Cancellation Policy */}
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <SectionLabel>🚫 Cancellation Policy</SectionLabel>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "center" }}>
                  {[
                    { label: "🟢 Flexible", value: "Flexible" },
                    { label: "🟡 Moderate", value: "Moderate" },
                    { label: "🔴 Strict",   value: "Strict" },
                  ].map(({ label, value }) => (
                    <FilterChip
                      key={value}
                      label={label}
                      selected={!!advancedFilters.cancelPolicies?.includes(value)}
                      onClick={() => toggleCancelPolicy(value)}
                    />
                  ))}
                </Box>
              </Box>

              {/* Rating + Reviews side by side */}
              <Box sx={{ display: "flex", gap: 2 }}>
                <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <SectionLabel>⭐ Min. Rating</SectionLabel>
                  <TextField
                    value={minReviewScore}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === "" || /^[1-4](\.\d{0,2})?$/.test(v)) setMinReviewScore(v);
                    }}
                    onBlur={() => {
                      const n = parseFloat(minReviewScore);
                      setMinReviewScore(isNaN(n) || n < 1 || n >= 5 ? "4.00" : n.toFixed(2));
                    }}
                    type="text"
                    size="small"
                    slotProps={{ htmlInput: { inputMode: "decimal", style: { textAlign: "center" } } }}
                    sx={{ width: "100%" }}
                  />
                </Box>
                <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <SectionLabel>💬 Min. Reviews</SectionLabel>
                  <TextField
                    value={minReviewCount}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === "" || (Number.isInteger(+v) && +v >= 0 && +v <= 5000))
                        setMinReviewCount(v === "" ? "" : parseInt(v, 10));
                    }}
                    onBlur={() => {
                      if (minReviewCount === "" || minReviewCount < 0) setMinReviewCount(5);
                      else if (minReviewCount > 5000) setMinReviewCount(5000);
                    }}
                    type="text"
                    size="small"
                    slotProps={{ htmlInput: { inputMode: "numeric", style: { textAlign: "center" } } }}
                    sx={{ width: "100%" }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </FilterSection>

        {/* ── 🛎️ Amenities ── */}
        <FilterSection noDivider>
          <SectionLabel>🛎️ Popular Amenities</SectionLabel>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "center" }}>
            <AnimatePresence>
              {sortChips(POPULAR_AMENITY_IDS).map((id) => {
                const key = `amenity_${id}`;
                const label = `${AMENITY_EMOJIS[id] || ""} ${AMENITIES_MAP[id] || key}`.trim();
                return (
                  <motion.div
                    key={key}
                    layout
                    initial={{ scale: 0.85, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.85, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <FilterChip
                      label={label}
                      selected={!!advancedFilters[key]}
                      onClick={() => toggleAmenity(key)}
                    />
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </Box>

          {/* More Amenities accordion */}
          <Accordion
            disableGutters
            elevation={0}
            sx={{ bgcolor: "transparent", mt: 2, "&:before": { display: "none" } }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                minHeight: 40,
                px: 0,
                justifyContent: "center",
                "& .MuiAccordionSummary-content": { flexGrow: 0, my: 0 },
              }}
            >
              <Typography variant="body2" fontWeight={600} color="text.secondary">
                Show more amenities
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 1.5, px: 0, pb: 0 }}>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "center" }}>
                <AnimatePresence>
                  {Object.entries(AMENITIES_MAP)
                    .filter(([id]) => !POPULAR_AMENITY_IDS.includes(Number(id)))
                    .sort(([idA, lA], [idB, lB]) => {
                      const aS = !!advancedFilters[`amenity_${idA}`];
                      const bS = !!advancedFilters[`amenity_${idB}`];
                      if (aS !== bS) return bS - aS;
                      return lA.localeCompare(lB);
                    })
                    .map(([id, label]) => {
                      const key = `amenity_${id}`;
                      const finalLabel = `${AMENITY_EMOJIS[id] || ""} ${label.split(":")[0]}`.trim();
                      return (
                        <motion.div
                          key={key}
                          layout
                          initial={{ scale: 0.85, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          exit={{ scale: 0.85, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <FilterChip
                            label={finalLabel}
                            selected={!!advancedFilters[key]}
                            onClick={() => toggleAmenity(key)}
                          />
                        </motion.div>
                      );
                    })}
                </AnimatePresence>
              </Box>
            </AccordionDetails>
          </Accordion>
        </FilterSection>
      </DialogContent>

      {/* ── Actions ── */}
      <DialogActions
        sx={{
          bgcolor: "background.paper",
          justifyContent: "center",
          gap: 2,
          px: 3,
          py: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Button
          variant="outlined"
          size="medium"
          color="secondary"
          onClick={() => setAdvancedFilters({})}
          sx={{ borderRadius: "10px", fontWeight: 600, px: 4, minWidth: 120 }}
        >
          Reset All
        </Button>
        <Button
          onClick={onClose}
          variant="contained"
          size="medium"
          sx={{ borderRadius: "10px", fontWeight: 600, px: 4, minWidth: 120 }}
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
}
